import React, { Component } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, IconButton, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Paper, Select, MenuItem
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel, Refresh, Add, Delete } from '@mui/icons-material';
import { connect } from 'react-redux';
import { getOnboardingConfig, updateOnboardingConfig } from '../../actions/onboarding';
import { validateOfferConfig } from '../../lib/welcomeOffer';

class SeedConfig extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      editing: false,
      error: null,
      success: null,
      config: null,
      editForm: {},
      // Editable trial arrays
      editSenderIds: [],
      editMessageTypes: [],
      editTemplates: [],
    };
    this.getConfigReq = false;
    this.updateConfigReq = false;
  }

  componentDidMount() {
    this.loadConfig();
  }

  loadConfig = () => {
    this.getConfigReq = true;
    this.setState({ loading: true, error: null, success: null });
    this.props.getOnboardingConfig('sms');
  }

  componentWillReceiveProps(nextProps) {
    if (this.getConfigReq && nextProps.onboardingConfig) {
      this.getConfigReq = false;
      if (nextProps.onboardingConfig.success && nextProps.onboardingConfig.config) {
        this.setState({ loading: false, config: nextProps.onboardingConfig.config });
      } else {
        this.setState({ loading: false, error: nextProps.onboardingConfig.error || 'Failed to load config' });
      }
      return;
    }
    if (this.updateConfigReq && nextProps.updateConfigResult) {
      this.updateConfigReq = false;
      if (nextProps.updateConfigResult.success && nextProps.updateConfigResult.config) {
        this.setState({
          saving: false,
          editing: false,
          success: 'Config updated successfully',
          config: nextProps.updateConfigResult.config,
        });
      } else {
        this.setState({
          saving: false,
          error: nextProps.updateConfigResult.error || 'Failed to update config',
        });
      }
      return;
    }
  }

  startEditing = () => {
    const { config } = this.state;
    this.setState({
      editing: true,
      success: null,
      error: null,
      editForm: {
        trial_days: config.trialDays,
        initial_balance: config.initialBalance,
        feature_transactional: config.featureTransactional,
        feature_promotional: config.featurePromotional,
        feature_otp: config.featureOtp,
        limit_max_sms_per_day: config.limitMaxSmsPerDay,
        demo_sender_id: config.demoSenderId || '',
        demo_header_type: config.demoHeaderType || '',
        demo_template_id: config.demoTemplateId || '',
        demo_message_type: config.demoMessageType || '',
        is_active: config.isActive,
        offer_enabled: !!config.offerEnabled,
        offer_percent: config.offerPercent || 0,
        offer_days: config.offerDays || 0,
        offer_code: config.offerCode || '',
      },
      editSenderIds: (config.trialSenderIds || []).map(s => ({ ...s })),
      editMessageTypes: (config.trialMessageTypes || []).map(m => ({ ...m })),
      editTemplates: (config.trialTemplates || []).map(t => ({ ...t })),
    });
  }

  cancelEditing = () => {
    this.setState({ editing: false, editForm: {}, editSenderIds: [], editMessageTypes: [], editTemplates: [], error: null });
  }

  handleFormChange = (field, value) => {
    this.setState(prev => ({ editForm: { ...prev.editForm, [field]: value } }));
  }

  // --- Trial array handlers ---
  handleArrayItemChange = (arrayKey, idx, field, value) => {
    this.setState(prev => {
      const arr = [...prev[arrayKey]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { [arrayKey]: arr };
    });
  }

  addArrayItem = (arrayKey, defaultItem) => {
    this.setState(prev => ({ [arrayKey]: [...prev[arrayKey], { ...defaultItem }] }));
  }

  removeArrayItem = (arrayKey, idx) => {
    this.setState(prev => ({ [arrayKey]: prev[arrayKey].filter((_, i) => i !== idx) }));
  }

  saveConfig = () => {
    const { editForm, editSenderIds, editMessageTypes, editTemplates } = this.state;

    const offerError = validateOfferConfig({
      enabled: editForm.offer_enabled,
      percent: editForm.offer_percent,
      days: editForm.offer_days,
      code: editForm.offer_code,
    });
    if (offerError) {
      this.setState({ error: offerError, success: null });
      return;
    }

    const body = { ...editForm, offer_code: String(editForm.offer_code || '').trim() };

    // Map trial arrays to API format (snake_case keys)
    if (editSenderIds.length > 0) {
      body.trial_sender_ids = editSenderIds.map(s => ({
        sender_id: s.senderId || s.sender_id || '',
        sender_name: s.senderName || s.sender_name || '',
        header_type: s.headerType || s.header_type || 'alphanumeric',
        is_default: !!s.isDefault || !!s.is_default,
        max_tps: parseInt(s.maxTps || s.max_tps) || 10,
      }));
    }
    if (editMessageTypes.length > 0) {
      body.trial_message_types = editMessageTypes.map(m => ({
        message_type: m.messageType || m.message_type || '',
        header_type: m.headerType || m.header_type || 'alphanumeric',
        description: m.description || '',
        is_default: !!m.isDefault || !!m.is_default,
        priority: parseInt(m.priority) || 1,
      }));
    }
    if (editTemplates.length > 0) {
      body.trial_templates = editTemplates.map(t => ({
        template_name: t.templateName || t.template_name || '',
        template_body: t.templateBody || t.template_body || '',
        message_type: t.messageType || t.message_type || 'implicit',
        sender_id: t.senderId || t.sender_id || '',
        is_default: !!t.isDefault || !!t.is_default,
      }));
    }

    this.updateConfigReq = true;
    this.setState({ saving: true, error: null, success: null });
    this.props.updateOnboardingConfig('sms', body);
  }

  renderField = (label, value) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? '--'}</Typography>
    </Box>
  )

  renderEditField = (label, field, type = 'text') => {
    const { editForm } = this.state;
    if (type === 'boolean') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Switch
            checked={!!editForm[field]}
            onChange={(e) => this.handleFormChange(field, e.target.checked)}
            size="small"
          />
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{label}</Typography>
        <TextField
          value={editForm[field] ?? ''}
          onChange={(e) => this.handleFormChange(field, type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
          size="small"
          variant="outlined"
          type={type}
          sx={{ width: 200 }}
        />
      </Box>
    );
  }

  renderTrialArray = (title, items, columns) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">No items configured</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.key} sx={{ fontWeight: 600, fontSize: 12 }}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={idx}>
                  {columns.map(col => (
                    <TableCell key={col.key} sx={{ fontSize: 12 }}>
                      {col.render ? col.render(item[col.key], item) : (
                        typeof item[col.key] === 'boolean'
                          ? <Chip label={item[col.key] ? 'Yes' : 'No'} size="small" color={item[col.key] ? 'success' : 'default'} variant="outlined" />
                          : (item[col.key] ?? '--')
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  renderEditableTrialArray = (title, arrayKey, items, columns, defaultItem) => {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => this.addArrayItem(arrayKey, defaultItem)}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No items. Click Add to create one.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map(col => (
                    <TableCell key={col.key} sx={{ fontWeight: 600, fontSize: 12 }}>{col.label}</TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 50 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={idx}>
                    {columns.map(col => (
                      <TableCell key={col.key} sx={{ fontSize: 12, py: 0.5 }}>
                        {col.type === 'boolean' ? (
                          <Switch
                            checked={!!item[col.key]}
                            onChange={(e) => this.handleArrayItemChange(arrayKey, idx, col.key, e.target.checked)}
                            size="small"
                          />
                        ) : col.type === 'select' ? (
                          <Select
                            value={item[col.key] || col.options[0]}
                            onChange={(e) => this.handleArrayItemChange(arrayKey, idx, col.key, e.target.value)}
                            size="small"
                            sx={{ fontSize: 12, minWidth: 120 }}
                          >
                            {col.options.map(opt => (
                              <MenuItem key={opt} value={opt} sx={{ fontSize: 12 }}>{opt}</MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <TextField
                            value={item[col.key] ?? ''}
                            onChange={(e) => this.handleArrayItemChange(arrayKey, idx, col.key,
                              col.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
                            size="small"
                            variant="standard"
                            type={col.type || 'text'}
                            sx={{ fontSize: 12 }}
                            fullWidth
                            InputProps={{ sx: { fontSize: 12 } }}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ py: 0.5 }}>
                      <IconButton size="small" color="error" onClick={() => this.removeArrayItem(arrayKey, idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }

  render() {
    const { loading, saving, editing, error, success, config } = this.state;

    return (
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={this.props.onClose} variant="text">
            Back
          </Button>
          <Typography variant="h6">Onboarding Default Config</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <IconButton onClick={this.loadConfig} disabled={loading}>
              <Refresh />
            </IconButton>
            {!editing ? (
              <Button startIcon={<Edit />} onClick={this.startEditing} variant="outlined" disabled={loading || !config}>
                Edit
              </Button>
            ) : (
              <>
                <Button startIcon={<Cancel />} onClick={this.cancelEditing} variant="outlined" disabled={saving}>
                  Cancel
                </Button>
                <Button startIcon={<Save />} onClick={this.saveConfig} variant="contained" disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => this.setState({ success: null })}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !config ? (
          <Alert severity="warning">No config found for workspace type "sms"</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* Demo Account Config */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Demo Account</Typography>
                <Typography variant="caption" color="text.secondary">Shared demo SMS config used for demo sends</Typography>
                <Divider sx={{ my: 1 }} />
                {editing ? (
                  <>
                    {this.renderEditField('Sender ID', 'demo_sender_id')}
                    {this.renderEditField('Header Type', 'demo_header_type')}
                    {this.renderEditField('Template ID', 'demo_template_id')}
                    {this.renderEditField('Message Type', 'demo_message_type')}
                  </>
                ) : (
                  <>
                    {this.renderField('Sender ID', config.demoSenderId)}
                    {this.renderField('Header Type', config.demoHeaderType)}
                    {this.renderField('Template ID', config.demoTemplateId)}
                    {this.renderField('Message Type', config.demoMessageType)}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Trial Settings */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Trial Settings</Typography>
                <Typography variant="caption" color="text.secondary">Defaults for new trial tenants</Typography>
                <Divider sx={{ my: 1 }} />
                {editing ? (
                  <>
                    {this.renderEditField('Trial Days', 'trial_days', 'number')}
                    {this.renderEditField('Initial Balance', 'initial_balance', 'number')}
                    {this.renderEditField('Max SMS / Day', 'limit_max_sms_per_day', 'number')}
                    {this.renderEditField('Active', 'is_active', 'boolean')}
                  </>
                ) : (
                  <>
                    {this.renderField('Trial Days', config.trialDays)}
                    {this.renderField('Initial Balance', config.initialBalance)}
                    {this.renderField('Max SMS / Day', config.limitMaxSmsPerDay)}
                    {this.renderField('Active', config.isActive ? 'Yes' : 'No')}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Feature Flags */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Feature Flags</Typography>
                <Divider sx={{ my: 1 }} />
                {editing ? (
                  <>
                    {this.renderEditField('Transactional', 'feature_transactional', 'boolean')}
                    {this.renderEditField('Promotional', 'feature_promotional', 'boolean')}
                    {this.renderEditField('OTP', 'feature_otp', 'boolean')}
                  </>
                ) : (
                  <>
                    {this.renderField('Transactional', <Chip label={config.featureTransactional ? 'Enabled' : 'Disabled'} size="small" color={config.featureTransactional ? 'success' : 'default'} variant="outlined" />)}
                    {this.renderField('Promotional', <Chip label={config.featurePromotional ? 'Enabled' : 'Disabled'} size="small" color={config.featurePromotional ? 'success' : 'default'} variant="outlined" />)}
                    {this.renderField('OTP', <Chip label={config.featureOtp ? 'Enabled' : 'Disabled'} size="small" color={config.featureOtp ? 'success' : 'default'} variant="outlined" />)}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Workspace Info */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Workspace</Typography>
                <Divider sx={{ my: 1 }} />
                {this.renderField('Type', config.workspaceType)}
              </CardContent>
            </Card>

            {/* Welcome Offer (SS-13) */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Welcome Offer</Typography>
                <Typography variant="caption" color="text.secondary">Discount on the first credit purchase, given to each new self-signup org</Typography>
                <Divider sx={{ my: 1 }} />
                {editing ? (
                  <>
                    {this.renderEditField('Enabled', 'offer_enabled', 'boolean')}
                    {this.renderEditField('Percent Off (1-100)', 'offer_percent', 'number')}
                    {this.renderEditField('Days (1-90)', 'offer_days', 'number')}
                    {this.renderEditField('Code (optional)', 'offer_code')}
                  </>
                ) : (
                  <>
                    {this.renderField('Enabled', <Chip label={config.offerEnabled ? 'Enabled' : 'Disabled'} size="small" color={config.offerEnabled ? 'success' : 'default'} variant="outlined" />)}
                    {this.renderField('Percent Off', config.offerPercent ? `${config.offerPercent}%` : '--')}
                    {this.renderField('Days', config.offerDays || '--')}
                    {this.renderField('Code', config.offerCode || '--')}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Trial Sender IDs - full width */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              {editing ? this.renderEditableTrialArray(
                'Trial Sender IDs', 'editSenderIds', this.state.editSenderIds,
                [
                  { key: 'senderId', label: 'Sender ID' },
                  { key: 'senderName', label: 'Sender Name' },
                  { key: 'headerType', label: 'Header Type', type: 'select', options: ['alphanumeric', 'numeric', 'shortcode'] },
                  { key: 'isDefault', label: 'Default', type: 'boolean' },
                  { key: 'maxTps', label: 'Max TPS', type: 'number' },
                ],
                { senderId: '', senderName: '', headerType: 'alphanumeric', isDefault: false, maxTps: 10 }
              ) : this.renderTrialArray('Trial Sender IDs', config.trialSenderIds, [
                { key: 'senderId', label: 'Sender ID' },
                { key: 'senderName', label: 'Sender Name' },
                { key: 'headerType', label: 'Header Type' },
                { key: 'isDefault', label: 'Default' },
                { key: 'maxTps', label: 'Max TPS' },
              ])}
            </Box>

            {/* Trial Message Types - full width */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              {editing ? this.renderEditableTrialArray(
                'Trial Message Types', 'editMessageTypes', this.state.editMessageTypes,
                [
                  { key: 'messageType', label: 'Message Type', type: 'select', options: ['transactional', 'promotional', 'otp', 'service'] },
                  { key: 'headerType', label: 'Header Type', type: 'select', options: ['alphanumeric', 'numeric', 'shortcode'] },
                  { key: 'description', label: 'Description' },
                  { key: 'isDefault', label: 'Default', type: 'boolean' },
                  { key: 'priority', label: 'Priority', type: 'number' },
                ],
                { messageType: 'transactional', headerType: 'alphanumeric', description: '', isDefault: false, priority: 1 }
              ) : this.renderTrialArray('Trial Message Types', config.trialMessageTypes, [
                { key: 'messageType', label: 'Message Type' },
                { key: 'headerType', label: 'Header Type' },
                { key: 'description', label: 'Description' },
                { key: 'isDefault', label: 'Default' },
                { key: 'priority', label: 'Priority' },
              ])}
            </Box>

            {/* Trial Templates - full width */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              {editing ? this.renderEditableTrialArray(
                'Trial Templates', 'editTemplates', this.state.editTemplates,
                [
                  { key: 'templateName', label: 'Template Name' },
                  { key: 'templateBody', label: 'Template Body' },
                  { key: 'messageType', label: 'Category', type: 'select', options: ['implicit', 'explicit', 'numeric'] },
                  { key: 'senderId', label: 'Sender ID' },
                  { key: 'isDefault', label: 'Default', type: 'boolean' },
                ],
                { templateName: '', templateBody: '', messageType: 'implicit', senderId: '', isDefault: false }
              ) : this.renderTrialArray('Trial Templates', config.trialTemplates, [
                { key: 'templateName', label: 'Template Name' },
                { key: 'templateBody', label: 'Template Body' },
                { key: 'messageType', label: 'Message Type' },
                { key: 'senderId', label: 'Sender ID' },
                { key: 'isDefault', label: 'Default' },
              ])}
            </Box>
          </Box>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    onboardingConfig: state.onboarding.onboardingConfig,
    updateConfigResult: state.onboarding.updateConfigResult,
  };
}

export default connect(mapStateToProps, { getOnboardingConfig, updateOnboardingConfig })(SeedConfig);
